import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import Link from "src/components/PartnerLink";
import QuotationDetail from "./QuotationDetail";
import { useParams } from "react-router-dom";
import { useMemberInfoState } from "src/recoil/member/hook";
import { BaseModal } from "src/components";
import IssueModalRenderer from "./components/IssueModalRenderer";
import { Modal } from "src/pages/visitor/visitor-types";
import { useEffect, useState } from "react";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { QuotationItem } from "src/api/reservation/reservation-type";
import useProductFacility from "src/pages/product/hooks/useProductFacility";
import { useApiOperation } from "src/api/hooks";
import { postContractApplyAsync, getContractsAsync, patchContractCancelAsync } from "src/api/contract/contract-api";
import { getQuotationListAsync, getReservationsAsync } from "src/api/reservation/reservation-api";
import { BuildingModel, CommonFacilityModel } from "src/api/building/building-types";
import ContractApplicationForm from "src/pages/mypage/contract/contractApplication/ContractApplicationForm";
import { MbMember } from "src/api/member/member-types";
import { ApplicationFormModel, ContractRequest } from "src/api/contract/contract-types";

/**
 * 이용신청 화면
 */
const QuotationVerifyPage = () => {
  const { member } = useMemberInfoState();
  const { quotationNo } = useParams();

  const { goBack } = useNavigateGoBack();
  const { openErrorModal } = useErrorModal();
  const navigate = usePartnerNavigate();

  const [alertModal, setAlertModal] = useState<Modal>({
    isOpen: false,
  });

  const [confirmModal, setConfirmModal] = useState<Modal>({
    isOpen: false,
  });

  const [quotation, setQuotation] = useState<QuotationItem>();
  const [isContractFlowCompleted, setIsContractFlowCompleted] = useState(false);

  const { product, facility, building } = useProductFacility({
    productId: String(quotation?.productId || ""),
    facilityId: String(quotation?.buildingCommonFacilityId || ""),
  });

  const { executeAsync: getQuotaion } = useApiOperation(getQuotationListAsync);
  const { executeAsync: postContractApply } = useApiOperation(postContractApplyAsync, { noHandleError: true });
  const { executeAsync: getContracts } = useApiOperation(getContractsAsync, { noHandleError: true });
  const { executeAsync: getReservations } = useApiOperation(getReservationsAsync);
  const { executeAsync: patchContractCancel } = useApiOperation(patchContractCancelAsync);

  const [savedFormData, setSavedFormData] = useState<ApplicationFormModel>({
    richText: "",
    attachments: [],
  });

  // 견적서 상세 조회
  useEffect(() => {
    const fetchQuotationDetail = async () => {
      const { data, status } = await getQuotaion({ quotationNo: String(quotationNo || "") });
      if (status >= 200 && status <= 299) {
        const result = data.data.content[0];
        setQuotation(result.quotationItemList[0]);
      }
    };

    if (quotationNo) {
      fetchQuotationDetail();
    }
  }, [quotationNo]);

  // 계약 신청 및 계약 상태 확인 (두 useEffect를 하나로 합침)

  const handleContractFlow = async (formData?: ApplicationFormModel) => {
    // 계약 아이디가 없고 필요한 데이터가 있으면 계약 신청 진행
    if (product?.id && quotation?.quotationId) {
      // 계약 로딩 화면 표시

      setIsContractFlowCompleted(true);
      const newContractRequest: ContractRequest = {
        productId: product.id,
        applicationHeader: {
          mbOrganizationId: 0,
          memberNo: member?.memberNo || "",
          quotationId: Number(quotation.quotationId),
        },
        applicationBody: {
          applicantName: member?.nickname || member?.email,
          useStartTime: quotation.startDate,
          useEndTime: quotation.endDate,
          isAgreeTerms: true,
          ...(product?.isUseApplication &&
            formData && {
              formData: { richText: formData.richText, attachmentConfig: formData.attachmentConfig },
              attachments: formData.attachments,
            }),
        },
      };
      // 계약 신청
      const { data, status } = await postContractApply(newContractRequest);

      if (status >= 200 && status <= 299) {
        await checkContractStatus(data.data.contractId);
      } else {
        const { errorCode, errorMessage, errorData } = data.meta;

        if (errorCode === "eCT217") {
          setAlertModal({
            isOpen: true,
            title: "잘못된 요청입니다.",
            message: "유효하지 않은 페이지입니다",
          });
        } else {
          openErrorModal({ errorCode, errorMessage, errorData, statusCode: status });
        }
      }
    }
  };

  // 상품 승인 타입이 "자동승인" or 관리자 승인 && 이용신청 사용 여부가 false 면 계약 플로우 진행
  useEffect(() => {
    // 자동승인이지만 이용신청서: 사용이면 이용신청서 필요
    // 이용신청 미사용 이면 곧바로 계약 생성 관리자 승인이지만 미사용이면 pass /

    if (!product?.isUseApplication) {
      // if (product?.approvalType === "AUTO" || (product?.approvalType === "ADMIN" && !product?.isUseApplication)) {
      handleContractFlow();
    }
  }, [product?.isUseApplication]);

  // 계약 상태 확인
  const checkContractStatus = async (contractId: string) => {
    const { data, status } = await getContracts({ memberNo: String(member?.memberNo), contractIds: contractId });
    if (status >= 200 && status <= 299) {
      const contract = data.data.content[0];
      // 계약체결 상태 일때 예약상태 호출
      if (contract && contract.contractStep === "CONTRACT_ACCEPT") {
        await checkReservationStatus(contractId);
      } else {
        navigate(`/court/mypage/contracts/${contractId}`, {
          state: "quotation",
        });
      }
    }
  };

  // 예약 상태 확인
  const checkReservationStatus = async (contractId: string) => {
    let pollingId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    // 모든 타이머 정리

    const clearAllTimers = () => {
      if (pollingId) {
        clearTimeout(pollingId);
        pollingId = null;
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const pollingReservation = async () => {
      const { data, status } = await getReservations({ contractId, organizerOnly: true });

      if (status >= 200 && status <= 299) {
        const reservation = data.data.content[0];
        const {
          status: { code: statusCode },
          incompleteReasons,
        } = reservation;

        switch (statusCode) {
          // 예약 접수시 1초 후 예약 재확인
          case "ACCEPTED":
            pollingId = setTimeout(() => {
              pollingReservation();
            }, 1000);
            break;

          // 예약 점유 > 상세로 이동
          case "ACKNOWLEDGED":
            clearAllTimers();
            navigate(`/court/mypage/contracts/${contractId}`, {
              state: "quotation",
            });
            break;

          // 예약불가능 > 예약 불가능 표시 후 예약 취소
          case "INCOMPLETE":
            clearAllTimers();
            setConfirmModal({
              isOpen: true,
              title: "예약이 어려워요",
              message: "아래의 사유로 인해 예약이 어려운 상태입니다. 확인하신 후 다시 예약해주세요.",
              type: "fail",
              payload: incompleteReasons,
              onClose: () => {
                setConfirmModal({ isOpen: false });
                contractCancel(contractId);
              },
            });
            return;
          // 예약실패 > 예약 실패 표시 후 예약 취소
          case "CANCELLED":
            clearAllTimers();

            setConfirmModal({
              isOpen: true,
              title: "예약이 실패했습니다.\n다시 시도해주세요.",
              type: "cancel",
              onClose: () => {
                setConfirmModal({ isOpen: false });
                contractCancel(contractId);
              },
            });
            return;
        }
      }
    };

    // 15초 후 타임아웃 처리
    timeoutId = setTimeout(() => {
      // 이미 다른 모달이 표시되었거나 타이머가 취소된 경우 아무 작업도 하지 않음

      clearAllTimers();

      setConfirmModal({
        isOpen: true,
        title: "처리가 지연되고 있습니다.",
        message: "계속 기다리시거나 요청을 취소하고\n다시 시도하실 수 있습니다.",
        type: "delay",
        onClose: () => {
          setConfirmModal({ isOpen: false });
          contractCancel(contractId);
        },
        onClick: () => {
          setConfirmModal({ isOpen: false });
          // 재시도 시 처음부터 다시 시작
          checkReservationStatus(contractId);
        },
      });
    }, 15000);

    // 폴링 시작
    pollingReservation();

    // 컴포넌트 언마운트 시 모든 타이머 정리를 위한 클린업 함수 반환
    return () => {
      clearAllTimers();
    };
  };

  // 신청 취소
  const contractCancel = async (contractId: string) => {
    if (!contractId) {
      throw Error("contract?.contractId is not found");
    }
    const { data } = await patchContractCancel({ contractId: Number(contractId) });
    if (data?.data?.contractId) {
      navigate(`/court/mypage/contracts/${contractId}`, {
        state: "quotation",
      });
    }
  };

  // 상품 계약이 관리자 승인 && 이용신청 사용 여부가 true && 계약 플로우가 완료되지 않았으면 이용신청서 작성 화면 표시
  // if (product && product.approvalType === "ADMIN" && product.isUseApplication) {
  if (product && product.isUseApplication && !isContractFlowCompleted) {
    return (
      <>
        <ContractApplicationForm
          quotation={quotation as QuotationItem}
          facility={facility as CommonFacilityModel}
          building={building as BuildingModel}
          member={member as MbMember}
          onApplyContract={(formData?: ApplicationFormModel) => handleContractFlow(formData)}
          savedFormData={savedFormData}
          onChangeFormData={(formData: ApplicationFormModel) => setSavedFormData(formData)}
          productId={product.id}
        />

        {alertModal.isOpen && (
          <BaseModal isOpen={true} title={""} onClose={() => goBack()}>
            <div>
              <h2 className="mb20">{alertModal?.title}</h2>
              <p className="pre-formatted text-primary3" style={{ lineHeight: "1.5" }}>
                {alertModal?.message}
              </p>
              <div className="flex-center-center mt20">
                <button type="button" className="base-btn" style={{ width: 70, height: 40 }} onClick={() => goBack()}>
                  확인
                </button>
              </div>
            </div>
          </BaseModal>
        )}
        {confirmModal.isOpen && (
          <IssueModalRenderer
            isOpen={true}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type as "delay" | "existing" | "fail"}
            onClick={() => {
              confirmModal.onClick && confirmModal.onClick();
            }}
            onClose={() => (confirmModal.onClose ? confirmModal.onClose() : setConfirmModal({ isOpen: false }))}
            payload={confirmModal.payload}
          />
        )}
      </>
    );
  }
  if (isContractFlowCompleted) {
    return (
      <>
        <MetaTag title="이용신청 | TaapSpace" />
        <Header headerType="BACK" title={"예약하기"} rightArea={<Link to="/court/main" className="home"></Link>} />
        <QuotationDetail quotation={quotation as QuotationItem} facility={facility as CommonFacilityModel} building={building as BuildingModel} />
        {alertModal.isOpen && (
          <BaseModal isOpen={true} title={""} onClose={() => goBack()}>
            <div>
              <h2 className="mb20">{alertModal?.title}</h2>
              <p className="pre-formatted text-primary3" style={{ lineHeight: "1.5" }}>
                {alertModal?.message}
              </p>
              <div className="flex-center-center mt20">
                <button type="button" className="base-btn" style={{ width: 70, height: 40 }} onClick={() => goBack()}>
                  확인
                </button>
              </div>
            </div>
          </BaseModal>
        )}
        {confirmModal.isOpen && (
          <IssueModalRenderer
            isOpen={true}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type as "delay" | "existing" | "fail"}
            onClick={() => {
              confirmModal.onClick && confirmModal.onClick();
            }}
            onClose={() => (confirmModal.onClose ? confirmModal.onClose() : setConfirmModal({ isOpen: false }))}
            payload={confirmModal.payload}
          />
        )}
      </>
    );
  }

  return <></>;
};

export default QuotationVerifyPage;
